import { type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTrace, type TraceDetail } from "./use-traces.ts";
import { TraceWaterfall } from "./TraceWaterfall.tsx";
import { isoAttr, formatDateTime, formatTimeOfDay } from "../../lib/datetime.ts";
import "./traces.css";

/**
 * The Trace detail screen (task 11.11; design 12.2 "waterfall per trace", 12.3 drill-down). It is
 * the drill target of the trace list (`/traces/{id}`) and draws one trace's cross-service waterfall
 * from the REST read `GET /api/v1/traces/{id}` (role `viewer`).
 *
 * THE INDEPENDENT inferred/partial RULE: the trace's two independent booleans flow into the shared
 * `Waterfall` (via {@link TraceWaterfall}), which shows both badges when both apply, neither taking
 * precedence. Spans drill to their events; the honest `correlation` detail explains an inferred
 * trace.
 *
 * The detail read is keyed on the trace id (a closed trace is immutable), so it does not depend on
 * the live/pause state — opening a trace shows that exact trace regardless of the time-range.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the read is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - missing id: an address with no trace id is an honest empty state, not a crash or a request.
 * - loading / error / empty / ready as the query resolves. A 404 (no such trace) reads as a
 *   distinct empty cause; a cold-core waking error defers to the shell's banner.
 */
export function TraceDetailScreen(): ReactNode {
  const { id } = useParams();
  const { role } = useIdentity();
  const hasId = id !== undefined && id.length > 0;

  const { query } = useTrace(id, { enabled: hasId });
  const state = resolveState(hasId, role, query);

  return (
    <ScreenScaffold
      title="Trace"
      subtitle="The cross-service waterfall for one trace. Trace-level inferred and partial labels are independent; a span drills to its events."
      state={state}
    >
      <p className="trc-back">
        <Link to="/traces">← Back to all traces</Link>
      </p>
      {query.data !== undefined && <TraceDetailBody detail={query.data} />}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from id presence, role and query status. */
function resolveState(
  hasId: boolean,
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useTrace>["query"],
): ScreenState {
  // Least privilege first: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so this trace is hidden until it is. The server enforces access regardless."
          : "The traces view needs at least the viewer role.",
    };
  }

  // No id in the address: an honest empty state, never a crash or a request for a trace that can't exist.
  if (!hasId) {
    return {
      kind: "empty",
      cause: "No trace was named in the address. Choose a trace from the list to see its waterfall.",
    };
  }

  if (query.data !== undefined) return { kind: "ready" };

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" };
    if (query.error.kind === "not_found") {
      return {
        kind: "empty",
        cause: "There is no such trace, or it has aged out of retention. It may have expired since the list was loaded.",
      };
    }
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useTrace>["query"]["error"]): string {
  if (error === null) return "This trace could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this trace. The server enforces access.";
    case "network":
      return "The trace request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function TraceDetailBody(props: { detail: TraceDetail }): ReactNode {
  const { detail } = props;
  const generatedAt = detail.meta.generated_at;
  const sampling = detail.meta.data_quality.sampling_in_effect;

  return (
    <div className="trc">
      <p className="trc__meta">
        started{" "}
        <time dateTime={isoAttr(detail.started_at)}>
          {formatDateTime(detail.started_at)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
        {sampling && (
          <span className="trc__meta-flag" role="note">
            {" "}
            · sampling in effect. This trace may be partial
          </span>
        )}
      </p>

      <TraceWaterfall detail={detail} />
    </div>
  );
}
